
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import { formatDatesRangeText, sortDateRange } from "@/helpers/data";
import {
  BASE_COL_1,
  BASE_COL_2,
  DatePreset,
  FUTURE_COL_1,
} from "@/helpers/datePresets";
import {
  clearDateFilter,
  DateFilterPageKey,
  loadDateFilter,
  saveDateFilter,
  SavedDateFilter,
} from "@/helpers/filterPersistence";
import KeycloakSession from "@/helpers/keycloak-session";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

function parseRangeText(text: string): [string, string] | null {
  const parts = text.split(" - ");
  if (parts.length !== 2) return null;

  const from = parseMaskedDate(parts[0].trim());
  const to = parseMaskedDate(parts[1].trim());
  if (!from || !to) return null;

  const range: [string, string] = [from, to];
  sortDateRange(range);
  return range;
}

// Require DD/MM/YYYY so partial edits (e.g. 18/1/2025 while typing 12) are not
// treated as complete and auto-normalized mid-keystroke.
const MASKED_DATE_RE = /^\d{2}\/\d{2}\/\d{4}$/;

const MASKED_DATE_PARSE_FORMATS = [
  "DD/MM/YYYY",
  "D/M/YYYY",
  "DD/M/YYYY",
  "D/MM/YYYY",
];

function maskDateInput(raw: string): string {
  const cleaned = raw.replace(/[^\d/]/g, "");

  // Preserve slash-separated input (e.g. 5/5/2025) instead of re-digitizing it.
  if (cleaned.includes("/")) {
    const segments = cleaned.split("/");
    const day = (segments[0] || "").slice(0, 2);
    const monthRaw = segments[1] || "";
    const month = monthRaw.slice(0, 2);
    const year = (segments.slice(2).join("") + monthRaw.slice(2)).slice(0, 4);

    if (year.length > 0 || monthRaw.length > 2) {
      return `${day}/${month}/${year}`;
    }
    if (month.length >= 2) {
      return `${day}/${month}/`;
    }
    if (segments.length >= 2 || cleaned.endsWith("/")) {
      return `${day}/${month}`;
    }
    if (day.length >= 2) {
      return `${day}/`;
    }
    return day;
  }

  const digits = cleaned.slice(0, 8);
  if (digits.length <= 2) {
    return digits.length === 2 ? `${digits}/` : digits;
  }
  if (digits.length <= 4) {
    const masked = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return digits.length === 4 ? `${masked}/` : masked;
  }
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function isCompleteMaskedDate(text: string): boolean {
  return MASKED_DATE_RE.test(text.trim());
}

function parseMaskedDate(text: string): string | null {
  const trimmed = text.trim();
  if (!isCompleteMaskedDate(trimmed)) return null;

  for (const format of MASKED_DATE_PARSE_FORMATS) {
    const parsed = dayjs(trimmed, format, true);
    if (parsed.isValid()) return parsed.format("YYYY-MM-DD");
  }
  return null;
}

function formatMaskedDate(ymd: string): string {
  return dayjs(ymd).format("DD/MM/YYYY");
}

@Component
export default class DatePickerFilter extends Vue {
  @Prop(
    {
      type: Array,
      default: () => [
        new Date().toISOString().slice(0, 10),
        new Date().toISOString().slice(0, 10),
      ],
    } /* Today */
  )
  dateRange!: any;

  // Enables forward-looking presets (Tomorrow / Next 7 / Next 30) with a
  // reordered Quick column. Turn on for Appointments and Tasks.
  @Prop({ type: Boolean, default: false }) futurePresets!: boolean;

  // Page-level key under which the latest applied preset / custom range is
  // persisted. Null disables persistence — keeps this component reusable in
  // contexts that don't want to write to localStorage.
  @Prop({ type: String, default: null }) persistKey!: DateFilterPageKey | null;

  // When true, the activator text field shows the translated preset label
  // (e.g. "Last 7 days") only when the user explicitly picked a preset, and
  // falls back to the date range text for custom calendar / typed ranges.
  // When false (default) the activator always shows the date range text.
  @Prop({ type: Boolean, default: false }) showPresetLabel!: boolean;
  @Prop({ type: String, default: "date_range" }) label!: string;

  menu = false;
  dates: any = this.dateRange;
  dateRangeText: any = formatDatesRangeText(this.dates?.[0], this.dates?.[1]);

  // Last committed dates — i.e. what the parent currently knows about.
  // Used to revert if the user leaves the custom panel or closes the popover without applying.
  committedDates: any = this.cloneDates(this.dateRange);

  // Preset id when the user explicitly picked a Quick/Calendar preset; null when
  // the range was chosen via the custom calendar or typed into the activator.
  committedPresetId: string | null = null;

  // Expansion-panel index: 0 = Presets, 1 = Custom range.
  activePanel = 0;

  updateFromDateRange = false;
  updateFromDates = false;
  debounceTimeout = null as any;
  customRangeDebounceTimeout = null as any;
  customRangeFromInput = "";
  customRangeToInput = "";
  customRangeInputError: string | null = null;
  private syncingCustomRangeFromDates = false;

  private cloneDates(d: any): any {
    if (d == null) return null;
    return Array.isArray(d) ? [...d] : d;
  }

  private commitAndEmit() {
    this.committedDates = this.cloneDates(this.dates);
    this.$emit("update-filter", this.dates);
  }

  private revertToCommitted() {
    // Drop any pending typed-range parse so a late debounce can't re-commit
    // the abandoned input after we've reverted.
    clearTimeout(this.debounceTimeout);
    clearTimeout(this.customRangeDebounceTimeout);
    this.customRangeInputError = null;
    this.dates = this.cloneDates(this.committedDates);
    // Restore the activator's displayed text too — reverting `dates` alone
    // leaves the field showing the abandoned selection.
    this.syncRangeTextFromDates();
    this.syncCustomRangeTextFromDates();
  }

  private persistValue(value: SavedDateFilter | null) {
    if (!this.persistKey) return;
    if (value == null) {
      clearDateFilter(this.persistKey);
    } else {
      saveDateFilter(this.persistKey, value);
    }
  }

  private persistCurrentDatesAsCustom() {
    if (!this.persistKey) return;
    if (!Array.isArray(this.dates) || this.dates.length !== 2) return;
    const from = dayjs(this.dates[0]).format("YYYY-MM-DD");
    const to = dayjs(this.dates[1]).format("YYYY-MM-DD");
    if (!dayjs(from).isValid() || !dayjs(to).isValid()) return;
    this.persistValue({ kind: "custom", from, to, v: 1 });
  }

  get popoverWidth(): string {
    return this.isEditorInPayments() ? "290px" : "520px";
  }

  // Scalar YYYY-MM-DD proxy for the editor single-date picker. A non-range
  // VDatePicker needs a string value (it calls .split on it); `dates` is
  // always an array internally, so we bridge here.
  get singleDate(): string | null {
    const first = Array.isArray(this.dates) ? this.dates[0] : this.dates;
    if (first == null) return null;
    const d = dayjs(first);
    return d.isValid() ? d.format("YYYY-MM-DD") : null;
  }

  set singleDate(value: string | null) {
    this.dates = value == null ? null : [value, value];
  }

  get col1(): DatePreset[] {
    return this.futurePresets ? FUTURE_COL_1 : BASE_COL_1;
  }

  get col2(): DatePreset[] {
    return BASE_COL_2;
  }

  get allPresets(): DatePreset[] {
    return [...this.col1, ...this.col2];
  }

  // i18n key of the committed preset's label — consumed by the activator and
  // the collapsed "Presets" panel header. Only set when the user picked a preset.
  get activePresetLabelKey(): string | null {
    if (!this.committedPresetId) return null;
    const preset = this.allPresets.find((p) => p.id === this.committedPresetId);
    return preset?.labelKey ?? null;
  }

  // Formatted from–to text of the committed preset's range, shown next to the
  // "Presets" header so users see the concrete dates a relative preset resolves to.
  get activePresetRangeText(): string | null {
    if (!this.committedPresetId) return null;
    if (!Array.isArray(this.dates) || this.dates.length !== 2) return null;
    return formatDatesRangeText(this.dates[0], this.dates[1]) as string | null;
  }

  // True only when the activator should render the preset label instead of
  // the literal date range — i.e. the host opted in via `showPresetLabel`
  // and the user explicitly committed a Quick/Calendar preset.
  get isShowingPresetLabel(): boolean {
    return this.showPresetLabel && this.committedPresetId !== null;
  }

  // Editors on the payments page may only pick a single day via the calendar;
  // block free-text input so they cannot type a multi-day range.
  get isActivatorReadonly(): boolean {
    return this.isShowingPresetLabel || this.isEditorInPayments();
  }

  // What the activator v-text-field renders. Either the translated preset
  // label (when enabled + matched) or the raw date range text (default).
  get activatorDisplayText(): string {
    if (this.isShowingPresetLabel) {
      return String(this.$t(this.activePresetLabelKey as string));
    }
    return this.dateRangeText ?? "";
  }

  // Bridge between the v-text-field's @input and the debounced parser. We
  // used to bind v-model="dateRangeText"; now the activator binds :value to
  // a computed, so we keep the parser working by mirroring typed input into
  // dateRangeText before invoking the existing handler.
  onActivatorInput(value: string) {
    if (this.isActivatorReadonly) return;
    this.dateRangeText = value;
    this.onDateRangeTextChanged();
  }

  onDateRangeTextChanged() {
    if (this.updateFromDates) {
      this.updateFromDates = false;
      return;
    }

    this.updateFromDateRange = true;

    clearTimeout(this.debounceTimeout);

    this.debounceTimeout = setTimeout(() => {
      const parsed = parseRangeText(this.dateRangeText);
      if (parsed) {
        this.dates = parsed;
        this.dateRangeText = formatDatesRangeText(
          this.dates[0],
          this.dates[1]
        );
        this.committedPresetId = null;
        this.commitAndEmit();
        this.persistCurrentDatesAsCustom();
      }
    }, 700);
  }

  get isCustomRangeApplyEnabled(): boolean {
    return this.parseCustomRangeFields() !== null;
  }

  private resolveCustomRangeInputError(): string {
    if (!this.customRangeFromInput.trim()) {
      return String(this.$t("custom_range_from_required"));
    }
    if (!this.customRangeToInput.trim()) {
      return String(this.$t("custom_range_to_required"));
    }
    return String(this.$t("custom_range_invalid"));
  }

  private syncCustomRangeTextFromDates() {
    if (this.dates == null) {
      this.customRangeFromInput = "";
      this.customRangeToInput = "";
      return;
    }

    const dateArray = Array.isArray(this.dates) ? this.dates : [this.dates];
    if (dateArray.length === 0) {
      this.customRangeFromInput = "";
      this.customRangeToInput = "";
      return;
    }

    this.syncingCustomRangeFromDates = true;

    if (dateArray.length === 1) {
      const ymd = dayjs(dateArray[0]).format("YYYY-MM-DD");
      if (dayjs(ymd).isValid()) {
        this.customRangeFromInput = formatMaskedDate(ymd);
        this.customRangeToInput = "";
      }
    } else {
      const sorted = [...dateArray.slice(0, 2)];
      sortDateRange(sorted);
      this.customRangeFromInput = formatMaskedDate(sorted[0]);
      this.customRangeToInput = formatMaskedDate(sorted[1]);
    }

    this.$nextTick(() => {
      this.syncingCustomRangeFromDates = false;
    });
  }

  private parseCustomRangeFields(): [string, string] | null {
    const from = parseMaskedDate(this.customRangeFromInput);
    const to = parseMaskedDate(this.customRangeToInput);
    if (!from || !to) return null;
    const range: [string, string] = [from, to];
    sortDateRange(range);
    return range;
  }

  private hasCompleteInvalidCustomRangeFields(): boolean {
    const fromComplete = isCompleteMaskedDate(this.customRangeFromInput);
    const toComplete = isCompleteMaskedDate(this.customRangeToInput);
    if (!fromComplete && !toComplete) return false;
    return (
      (fromComplete && !parseMaskedDate(this.customRangeFromInput)) ||
      (toComplete && !parseMaskedDate(this.customRangeToInput))
    );
  }

  onCustomRangeFromInput(value: string) {
    if (this.syncingCustomRangeFromDates) return;
    this.customRangeFromInput = maskDateInput(value);
    this.scheduleCustomRangeFieldParse();
  }

  onCustomRangeToInput(value: string) {
    if (this.syncingCustomRangeFromDates) return;
    this.customRangeToInput = maskDateInput(value);
    this.scheduleCustomRangeFieldParse();
  }

  private scheduleCustomRangeFieldParse() {
    this.customRangeInputError = null;
    clearTimeout(this.customRangeDebounceTimeout);
    this.customRangeDebounceTimeout = setTimeout(() => {
      this.tryApplyCustomRangeFieldsToCalendar();
    }, 300);
  }

  private tryApplyCustomRangeFieldsToCalendar(): boolean {
    const parsed = this.parseCustomRangeFields();
    if (parsed) {
      this.dates = parsed;
      const sorted = [...parsed];
      sortDateRange(sorted);
      this.syncingCustomRangeFromDates = true;
      this.customRangeFromInput = formatMaskedDate(sorted[0]);
      this.customRangeToInput = formatMaskedDate(sorted[1]);
      this.$nextTick(() => {
        this.syncingCustomRangeFromDates = false;
      });
      this.customRangeInputError = null;
      return true;
    }

    if (this.hasCompleteInvalidCustomRangeFields()) {
      this.customRangeInputError = String(this.$t("custom_range_invalid"));
    } else if (
      this.customRangeFromInput.trim() ||
      this.customRangeToInput.trim()
    ) {
      this.customRangeInputError = this.resolveCustomRangeInputError();
    } else {
      this.customRangeInputError = null;
    }
    return false;
  }

  get customRangeHint(): string | null {
    // Show the current range (or nothing) next to the CTA when a preset is committed.
    if (this.committedPresetId) return null;
    if (!this.dates || !Array.isArray(this.dates) || this.dates.length !== 2) {
      return null;
    }
    return formatDatesRangeText(this.dates[0], this.dates[1]) as string | null;
  }

  onMenuToggle(open: boolean) {
    if (open) {
      // Open the panel that matches the currently committed state: if the
      // user last applied a custom range (no preset matches), surface the
      // calendar immediately; otherwise show the presets.
      const committedIsCustomRange =
        Array.isArray(this.committedDates) &&
        this.committedDates.length === 2 &&
        this.committedPresetId === null;
      this.activePanel = committedIsCustomRange ? 1 : 0;
    } else {
      // Popover closed — discard any uncommitted picks from the custom panel.
      this.revertToCommitted();
    }
  }

  // Leaving the Custom panel (by expanding Presets) while the calendar had
  // unapplied picks would leak those picks into the parent's next submit.
  // Roll back to the last committed state whenever the user switches away
  // from the Custom panel without pressing Apply.
  @Watch("activePanel")
  onActivePanelChange(newVal: number, oldVal: number): void {
    if (oldVal === 1 && newVal !== 1) {
      this.revertToCommitted();
    } else if (newVal === 1) {
      this.customRangeInputError = null;
      this.syncCustomRangeTextFromDates();
    }
  }

  applyPreset(preset: DatePreset) {
    const range = preset.compute();
    this.dates = range == null ? null : [range[0], range[1]];
    this.syncRangeTextFromDates();
    this.updateFromDates = true;
    this.committedPresetId = preset.id;
    this.commitAndEmit();
    this.persistValue({ kind: "preset", id: preset.id, v: 1 });
    this.menu = false;
  }

  onCustomRangeChanged() {
    this.customRangeInputError = null;
    this.syncCustomRangeTextFromDates();
  }

  applyCustomRange() {
    clearTimeout(this.customRangeDebounceTimeout);

    const parsed = this.parseCustomRangeFields();
    if (!parsed) {
      this.customRangeInputError = this.resolveCustomRangeInputError();
      return;
    }

    this.dates = parsed;
    this.customRangeInputError = null;
    this.committedPresetId = null;
    this.selectFilter();
    this.persistCurrentDatesAsCustom();
    this.menu = false;
  }

  selectFilter() {
    if (this.updateFromDateRange) {
      this.updateFromDateRange = false;
      return;
    }

    this.updateFromDates = true;
    if (this.isEditorInPayments()) {
      this.committedPresetId = null;
    }
    this.commitAndEmit();

    this.syncRangeTextFromDates();
  }

  syncRangeTextFromDates() {
    if (this.isEditorInPayments()) {
      if (this.dates == null) {
        this.dateRangeText = formatDatesRangeText(null, null);
      } else if (this.dates.length === 1) {
        this.dateRangeText = this.isAdmin()
          ? formatDatesRangeText(this.dates[0], null)
          : formatDatesRangeText(this.dates, this.dates);
      } else if (this.dates.length === 2 && this.isAdmin()) {
        sortDateRange(this.dates);
        this.dateRangeText = formatDatesRangeText(this.dates[0], this.dates[1]);
      }
    } else {
      if (this.dates == null) {
        this.dateRangeText = formatDatesRangeText(null, null);
      } else if (this.dates.length === 1) {
        this.dateRangeText = formatDatesRangeText(this.dates[0], null);
      } else if (this.dates.length === 2) {
        sortDateRange(this.dates);
        this.dateRangeText = formatDatesRangeText(this.dates[0], this.dates[1]);
      }
    }
  }

  isEditorInPayments() {
    const kc = new KeycloakSession();
    if (
      kc.userHasRole("editor") &&
      !kc.userHasRole("admin") &&
      this.$route.name === "My Company"
    ) {
      return true;
    } else {
      return false;
    }
  }

  isAdmin() {
    const kc = new KeycloakSession();
    if (kc.userHasRole("admin")) {
      return true;
    } else if (kc.userHasRole("editor")) {
      return false;
    }
  }

  clearDateRange() {
    this.dates = null;
    this.dateRangeText = "";
    this.committedPresetId = null;
    this.selectFilter();
  }

  created() {
    if (!this.persistKey) return;
    const saved = loadDateFilter(this.persistKey);
    if (saved?.kind === "preset") {
      this.committedPresetId = saved.id;
    }
  }

  closeMenu() {
    // Explicit Cancel: discard uncommitted picks. Closing programmatically via
    // `menu = false` doesn't re-emit the menu's `input`, so onMenuToggle won't
    // fire — revert here so Cancel matches the click-outside behaviour.
    this.revertToCommitted();
    this.menu = false;
  }
}
